import { useEffect, useContext, useRef, useCallback } from "react";
import { useScrollCollapse } from "@utilities/useScrollCollapse";
import { getBreadcrumbRoutes } from "@utilities/breadcrumbRoutes";
import { LangContext, LangContextType } from "@utilities/customHooks";
import { useStickyHeader } from "./StickyHeaderContext";
import Breadcrumb from "./Breadcrumb";
import Tooltip from "./Tooltip";

interface FilterRenderProps {
  isExpanded: boolean;
  toggle: () => void;
}

interface StickyPageHeaderProps {
  title: string;
  titleTooltip?: string;
  actions?: React.ReactNode;
  /** Render prop — receives { isExpanded, toggle } for scroll-aware filter collapse */
  filters?: (props: FilterRenderProps) => React.ReactNode;
}

export default function StickyPageHeader({
  title,
  titleTooltip,
  actions,
  filters,
}: StickyPageHeaderProps) {
  const { langState } = useContext(LangContext) as LangContextType;
  const { setStickyHeaderActive } = useStickyHeader();
  const { isCompact, isExpanded, toggle, sentinelRef } = useScrollCollapse();
  const headerRef = useRef<HTMLDivElement>(null);

  // Tell Dashboard.tsx to skip its own breadcrumb
  useEffect(() => {
    setStickyHeaderActive(true);
    return () => setStickyHeaderActive(false);
  }, [setStickyHeaderActive]);

  // Publish the sticky header's total offset (top-bar + header height) as a CSS
  // variable on the scroll container so DataTable can position its sticky
  // table-header row right below without hardcoding pixel values.
  const updateCssVar = useCallback(() => {
    if (!headerRef.current) return;
    const height = headerRef.current.offsetHeight;
    // 56px = h-14 top bar
    const total = 56 + height;
    const container = headerRef.current.closest("[data-scroll-container]") as HTMLElement | null;
    if (container) {
      container.style.setProperty("--sticky-header-bottom", `${total}px`);
    }
  }, []);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(updateCssVar);
    observer.observe(el);
    updateCssVar();
    return () => observer.disconnect();
  }, [updateCssVar]);

  const breadcrumbRoutes = getBreadcrumbRoutes(langState as "eng" | "fr");

  return (
    <>
      {/* Sentinel — sits in normal flow above the sticky header.
          When it scrolls out of the viewport, IntersectionObserver triggers compact mode. */}
      <div ref={sentinelRef} className="h-px w-full" />

      <div
        ref={headerRef}
        className={`sticky top-14 z-40 bg-background transition-shadow duration-200 ${isCompact ? "shadow-sm border-b border-border" : ""}`}
      >
        {/* Breadcrumb */}
        <div
          className={`px-14 transition-[padding] duration-200 ease-out ${isCompact ? "pt-1 pb-1" : "pt-3 pb-6"}`}
        >
          <Breadcrumb breadcrumbRoutes={breadcrumbRoutes} />
        </div>

        {/* Title + Actions */}
        <div
          className={`px-14 transition-[padding] duration-200 ease-out ${isCompact ? "pb-2" : "pb-5"}`}
        >
          <div className="flex justify-between items-center gap-6 w-full">
            <div className="flex self-baseline min-w-0">
              <h1 className="font-bold text-3xl truncate">{title}</h1>
              {titleTooltip && <Tooltip content={titleTooltip} />}
            </div>
            {actions && <div className="flex shrink-0 gap-3">{actions}</div>}
          </div>
        </div>

        {/* Filter slot — render prop provides scroll-aware expand/collapse */}
        {filters && (
          <div className="px-14 pb-2">
            {filters({ isExpanded, toggle })}
          </div>
        )}
      </div>
    </>
  );
}
